import React, { useEffect, useMemo } from "react";
import { Box, Typography, Link as MenuLink } from "@mui/material";
import Stack from "@mui/material/Stack";
import { useNavigate } from "react-router";
import { withTranslation, WithTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchAlerts,
  markAsReadAlerts,
  fetchInitialUserAlerts,
  fetchNewAlerts,
} from "@/store/alerts/alertsAction";
import type { AlertsState } from "@/store/alerts/alertsReducer";
import InfiniteScroll from "react-infinite-scroll-component";
import CircularProgress from "@mui/material/CircularProgress";
import NotificationsBody from "@/components/Notifications";

// Rendered in three places: the header dropdown, the dashboard card, and the
// full /notifications page. `variant` and `isDropdown` are what tell them apart.
interface Props extends WithTranslation {
  isDropdown?: boolean;
  variant?: "dashboard" | string;
  filterType?: string;
  isMenuOpened?: boolean;
  close?: () => void;
}

// The store is still plain JS, so there is no generated RootState — this covers
// only the two slices this page reads.
type NotificationsRootState = {
  alertsReducer: AlertsState;
  user: { toJS: () => { id?: number } };
};

// components/Notifications declares `alertsList` as the hand-written Alert type
// in utils/types/alert and `user` as a SenderUser. This page has always passed
// the GraphQL card shape and the logged-in user instead, which the .jsx
// extension hid. Restating the real props in one place beats widening a chain of
// components this change does not otherwise touch — the mismatch is pre-existing
// and wants its own fix.
const AlertsList = NotificationsBody as unknown as React.FC<{
  alertsList: AlertsState["alerts"];
  close?: () => void;
  user: { id?: number };
}>;

const Notifications = (props: Props) => {
  const {
    t,
    tReady,
    isDropdown = false,
    variant,
    filterType,
    isMenuOpened,
    close,
    ...domProps
  } = props;
  const navigate = useNavigate();

  const showAllAlerts = (event?: React.MouseEvent) => {
    event?.preventDefault();
    navigate("/notifications");
    close?.();
  };

  const dispatch = useDispatch();
  const { alerts, loading, totalAlertsCount } = useSelector(
    (state: NotificationsRootState) => state.alertsReducer
  );

  const hasMoreItems = (alerts || []).length < totalAlertsCount;
  const user = useSelector((state: NotificationsRootState) => state.user.toJS());
  const alertsList = React.useMemo(() => alerts, [alerts]);
  // const alertsList = React.useMemo(() => {
  //   return (alerts ?? [])?.filter(alert => {
  //     // TODO enable ProjectTopicKudosAlert and ProjectTopicNoteAlert after get confirmation from Tuba
  //     return !([3,4,5]?.indexOf(alert?.alertTypeId) > -1);
  //   });
  // }, [alerts]);

  useEffect(() => {
    if (user && user.id && (alerts || []).length === 0 && !loading) {
      dispatch(fetchInitialUserAlerts(user.id));
    }
  }, [user, alerts, loading, dispatch]);

  // Refetch when the user opens the notifications menu so the dropdown is
  // fresh. This has to be fetchNewAlerts, not fetchInitialUserAlerts — the
  // latter no-ops once the user id is already in the store.
  useEffect(() => {
    if (isMenuOpened && user?.id) {
      dispatch(fetchNewAlerts(user.id));
    }
  }, [isMenuOpened, user?.id, dispatch]);

  useEffect(() => {
    if (alerts?.length > 0 && isMenuOpened && user && user.id) {
      const hasUnreadCount = alerts?.filter((alert) => !alert.isSeen)?.length;
      if (hasUnreadCount > 0) {
        dispatch(markAsReadAlerts(user.id));
      }
    }
  }, [alerts, isMenuOpened, user, dispatch]);

  const loadMore = () => {
    if (hasMoreItems && !loading) {
      const page = (alerts || []).length / 10 + 1;
      dispatch(fetchAlerts(user.id, page, 10));
    }
  };

  const scrollableTarget = useMemo(() => {
    if (variant === "dashboard")
      return "scrollable-dashboard-notifications-card";
    if (isDropdown) return "alerts-container";
    return "scrollableBody";
  }, [variant, isDropdown]);

  return (
    <Stack direction="column">
      {variant !== "dashboard" && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 3,
            py: 2,
            borderBottom: 1,
            borderBottomColor: (theme) => theme?.palette?.grey?.A400
          }}>
          <Typography variant="h2">{t("Notifications")}</Typography>
          {isDropdown && (
            <Box sx={{ ml: "auto" }}>
              {/* MUI's Link takes href, not react-router's `to` — the old
                  value was being forwarded to the anchor as an unknown DOM
                  attribute. showAllAlerts preventDefaults and navigates. */}
              <MenuLink href="#" onClick={showAllAlerts}>
                {t("SeeAll")}
              </MenuLink>
            </Box>
          )}
        </Box>
      )}
      <Stack direction="column">
        <Box
          {...domProps}
          id="alerts-container"
          sx={{
            maxHeight: !isDropdown ? "calc(100% - 10px)" : "360px",
            height: !isDropdown ? "calc(100% - 10px)" : "360px",
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: "5px",
              "&-track": {
                background: "transparent",
              },
              "&-thumb": {
                background: (theme) => theme.palette.grey["600"],
                "&:hover": {
                  background: (theme) => theme.palette.grey["600"],
                },
              },
            },
          }}
        >
          <InfiniteScroll
            dataLength={alertsList?.length}
            next={loadMore}
            hasMore={hasMoreItems}
            scrollableTarget={scrollableTarget}
            loader={
              loading && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pt: 2,
                    pb: 2
                  }}>
                  <CircularProgress />
                </Box>
              )
            }
          >
            <AlertsList alertsList={alertsList} close={close} user={user} />
          </InfiniteScroll>
        </Box>
      </Stack>
    </Stack>
  );
};

export default withTranslation(["translation.navigation"])(
  React.memo(Notifications)
);
